import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, FileWarning } from "lucide-react";
import { getToken } from "../utils/tokenStorage";

/**
 * GuidebookViewerPage — WebAdmin-only renderer for guidebook / blueprint HTML.
 *
 * The guidebook HTML files were relocated out of the public frontend folder
 * into a protected backend directory and are served ONLY via the authenticated
 * endpoint GET /api/admin/guidebooks/file/{filename}. This component mounts on
 * both:
 *   - the dedicated route  /admin/guidebook-viewer/:filename  (Library "Preview")
 *   - the SPA catch-all for any /<file>.html direct URL / internal cross-link
 * Either way we resolve the BARE filename (last path segment, never the full
 * pathname) so the API request is a clean, slash-free filename. Guards
 * (ProtectedRoute + WebAdminGuard) send non-auth -> /login, non-admin ->
 * /dashboard BEFORE this component renders.
 */
export default function GuidebookViewerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Prefer the :filename route param; otherwise take the last path segment of
  // the URL (handles the catch-all /<file>.html case). NEVER use the whole
  // pathname — the backend rejects any filename containing a slash.
  const rawName = params.filename || (location.pathname || "").split("/").filter(Boolean).pop() || "";
  const filename = decodeURIComponent(rawName);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = getToken();
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/guidebooks/file/${encodeURIComponent(filename)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {}, signal: controller.signal }
        );
        if (!res.ok) {
          if (!cancelled) setError(res.status === 404 ? "Guidebook not found." : "Unable to load this guidebook.");
          return;
        }
        let text = await res.text();
        // Make internal cross-links open in the parent window so they re-enter
        // the guarded SPA route (index → sub-guidebooks stay WebAdmin-gated).
        const baseTag = '<base target="_parent">';
        if (/<head[^>]*>/i.test(text)) {
          text = text.replace(/<head[^>]*>/i, (m) => `${m}\n${baseTag}`);
        } else {
          text = `${baseTag}\n${text}`;
        }
        if (!cancelled) setHtml(text);
      } catch (e) {
        if (!cancelled && e.name !== "AbortError") setError("Unable to load this guidebook.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; controller.abort(); };
  }, [filename]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-slate-500" data-testid="guidebook-viewer-loading">
        <Loader2 className="w-7 h-7 animate-spin mb-3" />
        <p className="text-sm">Loading guidebook…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-slate-600 px-6 text-center" data-testid="guidebook-viewer-error">
        <FileWarning className="w-10 h-10 text-amber-500 mb-3" />
        <h1 className="text-lg font-semibold text-slate-800 mb-1">{error}</h1>
        <p className="text-sm text-slate-500 mb-5 break-all max-w-md">{filename}</p>
        <button
          onClick={() => navigate("/admin/guidebooks")}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold text-white bg-sky-700 hover:bg-sky-800 transition-colors"
          data-testid="guidebook-viewer-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Guidebook Library
        </button>
      </div>
    );
  }

  return (
    <iframe
      title={filename}
      srcDoc={html}
      data-testid="guidebook-viewer-frame"
      style={{ position: "fixed", inset: 0, width: "100%", height: "100%", border: "none" }}
    />
  );
}
